import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { Text, Button, Box, ThemeIcon, useMantineTheme } from "@mantine/core";
import {
  IconBulb,
  IconEye,
  IconEyeglass2,
  IconFocusCentered,
  IconHelmetOff,
  IconShieldX,
} from "@tabler/icons-react";
import { isMobile, isFirefox } from "react-device-detect";
import { DataContext } from "../../../context/DataContext";
import VerificationProcessingScreen from "../../../components/verification-processing-screen";
import {
  requestCaptureChallenge,
  processAndUploadWithIntegrity,
  createVisibilityTracker,
  createTouchTracker,
  createMotionTracker,
} from "../../../utils/captureIntegrity";
import FirefoxCameraWarning from "../../../components/FirefoxCameraWarning";
import AndroidCameraBlockPortal, { fireAndroidBlockRecoveredIfPending } from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant } from "../../../utils/androidDetection";
import AgeEstimationStartDrawer from "../../../components/age-estimation-start-drawer";
import VeratadCapture from "./capture";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import useIsLandscape from "../../../hooks/useOrientation";
import { preloadFaceDetectionModels } from "../../../hooks/useFaceDetection";
import { trackEvent } from "../../../utils/telemetry";
import { landscapeEnabled } from "../../../utils/featureFlags";
import { getEdgeQueueUrl } from "../../../utils/edge";

// Stamped on every event this screen emits so the v1 / v2 funnels split
// without a join (v1 stamps "match_v1" on its landed / capture_started).
const FACE_UI = "match_v2";
const track = (name, props = {}, opts) => trackEvent(name, { face_ui: FACE_UI, ...props }, opts);

/**
 * Face capture for the face-to-ID-document match, v2.
 *
 * Same intro and capture experience as age estimation (agematch/estimation):
 * the camera view is mounted from the start, dormant, with the start drawer
 * over it; tapping the button wakes the camera and the drawer becomes the
 * live checklist. With the face landscape flag on, the drawer docks down the
 * right edge when the phone is held sideways, exactly as it does there.
 *
 * What is NOT borrowed from age estimation is the business logic. Upload
 * (integrity challenge, frame chain, interaction telemetry), the duplicate
 * guard, the embedded `onCompletion` hand-back to the document flow and the
 * standalone processing + polling are the v1 face-capture behavior
 * (./index.js), unchanged. Copy comes from the face-capture locale's
 * `drawer` and `error` sections so nothing on screen is age specific.
 *
 * Selected by FACE_MATCH_CAPTURE_V2 (see ./index.js); v1 stays the default.
 */
export default function VeratadFaceCaptureV2({ onCompletion }) {
  const { data, settings, dispatch, text, postPayloadMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("face-capture");

  const [hasStarted, setHasStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReadyForStatusCheck, setIsReadyForStatusCheck] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [cameraErrorName, setCameraErrorName] = useState(null);
  const [fallbackInProgress, setFallbackInProgress] = useState(false);
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(isMobile && isFirefox);
  const [gatingState, setGatingState] = useState({});

  const loadingStartTimeRef = useRef(null);
  const uploadInFlightRef = useRef(false);
  const challengeRef = useRef(null);
  const sessionTimestampsRef = useRef({ session_start: Date.now() });
  const telemetryRef = useRef(null);
  const captureFailedFiredRef = useRef(false);
  const fallbackUploadPendingRef = useRef(false);

  const destroyTrackers = () => {
    if (!telemetryRef.current) return;
    telemetryRef.current.visibility?.destroy?.();
    telemetryRef.current.touch?.destroy?.();
    telemetryRef.current.motion?.destroy?.();
    telemetryRef.current = null;
  };
  useEffect(() => destroyTrackers, []);

  // ── Layout: drawer under the oval, or beside it when held sideways ──
  const landscape = useIsLandscape();
  const sideLayout = landscapeEnabled() && isMobile && landscape;
  const [drawerEl, setDrawerEl] = useState(null);
  const [drawerInset, setDrawerInset] = useState(0);
  useEffect(() => {
    const el = drawerEl;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setDrawerInset(sideLayout ? rect.width + 12 : rect.height + 16);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [drawerEl, hasStarted, sideLayout]);

  // ── Models preload on intro mount (button waits on it) ──
  const [modelsReady, setModelsReady] = useState(false);
  useEffect(() => {
    track("landed", {}, { once: true });
    let cancelled = false;
    preloadFaceDetectionModels()
      .then(() => { if (!cancelled) setModelsReady(true); })
      .catch(() => { if (!cancelled) setModelsReady(true); });
    return () => { cancelled = true; };
  }, []);

  // ── Status polling (standalone route only; embedded returns first) ──
  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    if (processState === "NEXT_APP_TRIGGER" || processState === "COMPLETED" ||
        status === "PASS" || status === "FAIL") {
      track("verification_done", { status, process_state: processState });
    }
    if (processState === "NEXT_APP_TRIGGER") { setTerminalStatus("NEXT_APP"); return false; }
    if (processState === "COMPLETED") { setTerminalStatus(status === "PASS" ? "PASS" : "FAIL"); return false; }
    if (status === "PASS") { setTerminalStatus("PASS"); return false; }
    if (status === "FAIL") { setTerminalStatus("FAIL"); return false; }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  // data.edge is fixed for the life of a session, so the branch is stable
  // across renders (same pattern as v1 and age estimation).
  if (data?.edge === true) {
    useEdgeStatusCheck(isReadyForStatusCheck, captureTerminal);
  } else {
    useStatusCheck(isReadyForStatusCheck, false, "face-capture/veratad", captureTerminal);
  }

  // ── Start: wake the camera, start the integrity session ──
  const handleStart = () => {
    if (showFirefoxPrompt) return;
    uploadInFlightRef.current = false;
    sessionTimestampsRef.current.capture_start = Date.now();
    track("capture_started");
    if (postPayloadMessage) {
      postPayloadMessage({ event: "GET_STARTED_CLICKED", app: "face-capture/veratad" });
    }
    telemetryRef.current = {
      visibility: createVisibilityTracker(),
      touch: createTouchTracker(),
      motion: createMotionTracker(),
    };
    if (settings?.request_id) {
      requestCaptureChallenge(settings.request_id)
        .then((c) => { challengeRef.current = c; })
        .catch((err) => console.warn("[CaptureIntegrity] Challenge request failed:", err));
    }
    setHasStarted(true);
  };

  const handleReady = useCallback((cameraSignals) => {
    if (cameraSignals && telemetryRef.current) {
      telemetryRef.current.camera = cameraSignals;
    }
    fireAndroidBlockRecoveredIfPending({
      requestId: settings.request_id,
      captureType: "face",
      isBrowserHandoffSession: false,
    });
  }, [settings?.request_id]);

  const handleError = useCallback((err) => {
    console.error("[FaceCaptureV2] handleError:", JSON.stringify(err));
    if (!captureFailedFiredRef.current) {
      captureFailedFiredRef.current = true;
      track("face_capture_failed", {
        provider: "veratad",
        error_type: err?.type || "unknown",
        error_name: err?.name || err?.message || "unknown",
      });
    }
    setIsLoading(false);
    destroyTrackers();
    // Camera-permission failures get the camera-blocked recovery screens
    // (unblock steps + camera-app fallback), same routing as age estimation.
    if (err?.type === "camera_error") {
      setCameraErrorName(err?.name || null);
      setCameraBlocked(true);
      return;
    }
    setHasError(true);
  }, []);

  // ── Capture → upload (v1 pipeline) → hand back or process ──
  const handleCapture = async (blobIn, previewUrl, frameChain) => {
    // One upload per attempt: a stalled device can deliver several capture
    // callbacks (queued shutter taps, or auto-capture after a manual tap).
    if (uploadInFlightRef.current) {
      if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ } }
      return;
    }
    uploadInFlightRef.current = true;
    loadingStartTimeRef.current = Date.now();
    setIsLoading(true);
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ } }

    let blob = blobIn;
    if (typeof blob === "string") {
      try {
        const [header, b64] = blob.split(",");
        const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: mime });
      } catch (e) {
        console.error("[FaceCaptureV2] Failed to convert data URL to blob:", e);
        uploadInFlightRef.current = false;
        setIsLoading(false);
        return;
      }
    }

    try {
      const imageKey = `${settings.request_id}_dl_selfie.jpeg`;
      sessionTimestampsRef.current.capture_end = Date.now();
      track("upload_started", { bytes: blob?.size });
      const uploadStart = performance.now();
      await processAndUploadWithIntegrity(
        imageKey, blob, challengeRef.current, settings.request_id,
        "selfie", frameChain, sessionTimestampsRef.current, settings,
        telemetryRef.current
      );
      track("upload_completed", { upload_ms: Math.round(performance.now() - uploadStart) });

      if (fallbackUploadPendingRef.current) {
        fallbackUploadPendingRef.current = false;
        track("android_camera_os_block_fallback_uploaded", {
          capture_type: "face",
          is_browser_handoff: false,
          file_size_bytes: blob?.size,
          file_type: blob?.type,
        });
      }
      destroyTrackers();

      // Embedded in the document flow: hand back, the parent owns what follows.
      if (typeof onCompletion === "function") {
        onCompletion();
        return;
      }

      // Standalone route: trigger backend processing and poll for status.
      const payload = {
        ...data,
        request_id: settings.request_id,
        type: "SDK_SCAN",
        country: settings.country,
        front_ext: data?.front_ext || "jpeg",
        back_ext: data?.back_ext || "jpeg",
      };
      const endpoint =
        data?.edge === true
          ? getEdgeQueueUrl()
          : process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/estimate/process.php";
      const requestBody = data?.edge === true ? { ...payload, queue: "estimate" } : payload;

      track("processing_requested");
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      }).catch((err) => console.error("Process request failed:", err));

      const elapsed = Date.now() - loadingStartTimeRef.current;
      setTimeout(() => setIsReadyForStatusCheck(true), Math.max(0, 5000 - elapsed));

      if (postPayloadMessage) {
        postPayloadMessage({ event: "VERIFICATION_INITIATED", app: "face-capture/veratad" });
      }
    } catch (err) {
      console.error("[FaceCaptureV2] upload failed:", err);
      track("upload_failed", { error: err?.message });
      uploadInFlightRef.current = false;
      setIsLoading(false);
      setHasError(true);
    }
  };

  // Camera-app fallback (Android block portal): same pipeline, no frame chain.
  const handleSelfieFallback = (file) => {
    if (!file) return;
    fallbackUploadPendingRef.current = true;
    setFallbackInProgress(true);
    handleCapture(file, URL.createObjectURL(file), null);
  };

  // ── Drawer copy: publisher override → face-capture locale → English ──
  const d = locale?.drawer;
  const o = text?.faceMatch?.drawer;
  const pausedChecks = [
    { key: "lighting", icon: IconBulb, label: o?.paused?.lighting || d?.paused?.lighting || "Find\ngood light" },
    { key: "headwear", icon: IconHelmetOff, label: o?.paused?.headwear || d?.paused?.headwear || "Remove\nheadwear" },
    // The match is against the ID photo, so glasses stay on (v1 guidance).
    { key: "glasses", icon: IconEyeglass2, label: o?.paused?.glasses || d?.paused?.glasses || "Keep\nglasses on" },
  ];
  const inFrame = !!gatingState?.facePresent && !!gatingState?.faceSize && !!gatingState?.centering;
  const liveChecks = [
    { key: "lighting", icon: IconBulb, label: o?.live?.lighting || d?.live?.lighting || "Good lighting", satisfied: gatingState?.isTooDark === false },
    { key: "inFrame", icon: IconFocusCentered, label: o?.live?.inFrame || d?.live?.inFrame || "In frame", satisfied: inFrame },
    { key: "lookStraight", icon: IconEye, label: o?.live?.lookStraight || d?.live?.lookStraight || "Look straight", satisfied: !!gatingState?.pose },
  ];

  const startDrawer = (
    <AgeEstimationStartDrawer
      innerRef={setDrawerEl}
      title={!hasStarted ? o?.title || d?.title || "Get ready to match your face to your ID" : null}
      checks={hasStarted ? liveChecks : pausedChecks}
      showIndicators={hasStarted}
      compact={hasStarted}
      permissionCallout={
        !hasStarted
          ? o?.permissionCallout || d?.permissionCallout ||
            "We only use camera access to capture your face and match it to your ID."
          : null
      }
      showButton={!hasStarted}
      buttonLabel={o?.allowCameraButton || d?.allowCameraButton || "Allow Camera Access"}
      preparingLabel={o?.preparingLabel || d?.preparingLabel || "Getting ready…"}
      modelsReady={modelsReady}
      buttonTextColor={data?.styles?.components?.button?.color || "#fff"}
      onStart={handleStart}
      side={sideLayout}
    />
  );

  const centered = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: "400px",
    padding: "20px",
  };

  // ── Screens ──
  const forceAndroidBlock = new URLSearchParams(window.location.search).get("force_android_block");
  if (
    !fallbackInProgress &&
    (forceAndroidBlock === "os_toggle" || forceAndroidBlock === "chrome_permission" || forceAndroidBlock === "true")
  ) {
    return (
      <AndroidCameraBlockPortal
        locale={locale}
        requestId={settings.request_id}
        captureType="face"
        isBrowserHandoffSession={false}
        androidVariant={forceAndroidBlock === "chrome_permission" ? "chrome_permission_legacy" : "os_toggle_12plus"}
        onPhotoFallback={handleSelfieFallback}
        captureMode="user"
      />
    );
  }

  if (showFirefoxPrompt) {
    return <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />;
  }

  if (hasError) {
    return (
      <Box style={centered}>
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {locale?.error?.title || "Verification Failed"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {locale?.error?.message || "Something went wrong while uploading your image. Please try again."}
        </Text>
        <Button
          size="lg"
          mt="xl"
          onClick={() => {
            setHasError(false);
            setCameraBlocked(false);
            setCameraErrorName(null);
            setFallbackInProgress(false);
            captureFailedFiredRef.current = false;
            uploadInFlightRef.current = false;
            setHasStarted(true);
          }}
        >
          {locale?.error?.retry || "Try Again"}
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    // Embedded (document flow owns the post-selfie stages) gets the single
    // "Uploading your selfie…" stage; the standalone route gets the full
    // sequence. Same split as v1.
    return (
      <VerificationProcessingScreen
        type={onCompletion ? "selfie-upload" : "face-age"}
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  if (cameraBlocked && !fallbackInProgress) {
    if (cameraErrorName === "NotAllowedError") {
      return (
        <AndroidCameraBlockPortal
          locale={locale}
          requestId={settings.request_id}
          captureType="face"
          isBrowserHandoffSession={false}
          androidVariant={getAndroidBlockVariant()}
          onPhotoFallback={handleSelfieFallback}
          captureMode="user"
        />
      );
    }
    return (
      <Box style={centered}>
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {locale?.errors?.cameraErrorTitle || "Camera Access Denied"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {locale?.errors?.cameraDenied ||
            "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
        </Text>
        <Button
          size="lg"
          mt="xl"
          onClick={() => {
            setCameraBlocked(false);
            setCameraErrorName(null);
            captureFailedFiredRef.current = false;
            setHasStarted(true);
          }}
        >
          {locale?.error?.retry || "Try Again"}
        </Button>
      </Box>
    );
  }

  // Camera view mounted from the start, dormant until the button is tapped:
  // no getUserMedia call, no permission prompt, no timeout before that.
  return (
    <Box style={{ position: "relative", height: "100%" }}>
      <VeratadCapture
        onCapture={handleCapture}
        onError={handleError}
        onReady={handleReady}
        onPhotoFallback={handleSelfieFallback}
        requestId={settings.request_id}
        paused={!hasStarted}
        onGatingChange={setGatingState}
        bottomInset={hasStarted && !sideLayout ? drawerInset : 0}
        rightInset={sideLayout ? drawerInset : 0}
      />
      {startDrawer}
    </Box>
  );
}
